import React, { useDeferredValue, useState } from "react";
import clsx from "clsx";
import {
  Card,
  Text,
  SkeletonLoader,
  TextField,
  TextFieldVariant,
} from "@merge-api/merge-javascript-shared";
import { Search } from "lucide-react";
import {
  LinkedAccountSyncSelections,
  LinkedAccount,
  LinkedAccountPurviewLabel,
  UnreleasedAPICategory,
  APICategory,
} from "../../../../../../../models/Entities";
import EmptyStateWrapper from "../../../../../../shared/EmptyStateWrapper";
import LinkedAccountSensitivityLabelsCard from "../LinkedAccountSensitivityLabelsCard";
import { EMPTY_SELECTIONS, ROW_CLASS } from "./constants";
import useFilteredSyncSelections from "./hooks/useFilteredSyncSelections";
import SyncSelectionsHeaderRow from "./components/SyncSelectionsHeaderRow";
import SyncSelectionsList from "./components/SyncSelectionsList";

interface Props {
  category: APICategory | UnreleasedAPICategory;
  linkedAccountSyncSelections?: LinkedAccountSyncSelections;
  linkedAccount?: LinkedAccount;
  purviewLabels?: LinkedAccountPurviewLabel[];
  syncOneDriveContent?: boolean;
}

const LinkedAccountSyncSelectionsTable = ({
  category,
  linkedAccountSyncSelections,
  linkedAccount,
  purviewLabels,
  syncOneDriveContent,
}: Props) => {
  // state
  const [query, setQuery] = useState("");

  // hooks
  const deferredQuery = useDeferredValue(query);
  const { visibleSelections } = useFilteredSyncSelections({
    selections: linkedAccountSyncSelections ?? EMPTY_SELECTIONS,
    query: deferredQuery,
  });

  // derived state
  const integrationSlug = linkedAccount?.integration?.slug?.toLowerCase();
  const showSensitivityLabels =
    integrationSlug === "sharepoint" || integrationSlug === "microsoft-teams";

  // render
  return (
    <>
      {showSensitivityLabels && (
        <LinkedAccountSensitivityLabelsCard purviewLabels={purviewLabels} />
      )}

      <Card>
        <div className="flex flex-col px-6 py-5 gap-y-1">
          <Text variant="h4">Selected content</Text>
          <Text>Your users can choose content to selectively sync in Merge Link</Text>
          {syncOneDriveContent !== undefined && (
            <Text className="italic">
              {syncOneDriveContent
                ? "Syncing all OneDrive content"
                : "Not syncing OneDrive content"}
            </Text>
          )}
        </div>
        {!linkedAccountSyncSelections || linkedAccountSyncSelections.length > 0 ? (
          <>
            <div className="px-6 pb-5">
              <TextField
                aria-label="Search selections by name"
                placeholder="Search by name"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                variant={TextFieldVariant.Bordered}
                prefix={<Search size={16} className="mx-3 text-gray-40" />}
              />
            </div>
            <div
              role="table"
              aria-label="Selected content"
              aria-busy={!linkedAccountSyncSelections}
            >
              <SyncSelectionsHeaderRow category={category} />
              {!linkedAccountSyncSelections ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={`skeleton-row-${index}`}
                    role="row"
                    className={clsx(ROW_CLASS, "h-14", index > 0 && "border-t border-gray-10")}
                  >
                    <div role="cell" className="flex-1">
                      <SkeletonLoader fullWidth height={12} borderRadius={8} />
                    </div>
                  </div>
                ))
              ) : (
                <SyncSelectionsList
                  category={category}
                  selections={visibleSelections}
                  totalCount={linkedAccountSyncSelections.length}
                />
              )}
            </div>
          </>
        ) : (
          <div className="border-t border-gray-10 pt-5 pb-6">
            <EmptyStateWrapper title="All contents are currently shared for this Linked Account" />
          </div>
        )}
      </Card>
    </>
  );
};

export default LinkedAccountSyncSelectionsTable;
